import { Injectable, computed, inject, signal } from '@angular/core';
import { tap } from 'rxjs';
import { CementoParametroDto } from '../models/parametro-cemento.dto';
import { CementoParametroApiService } from '../services/cemento-parametro-api.service';
import { ServerListQuery } from '../shared/table/models/admin-server-table.model';

const DEFAULT_PAGE = 1;
const DEFAULT_ORDERING = 'name';

export interface CementoParametrosState {
  items: CementoParametroDto[];
  total: number;
  loading: boolean;
  query: ServerListQuery;
}

@Injectable({
  providedIn: 'root',
})
export class CementoParametrosStore {
  private readonly apiService = inject(CementoParametroApiService);

  // State
  private readonly state = signal<CementoParametrosState>({
    items: [],
    total: 0,
    loading: false,
    query: {
      page: DEFAULT_PAGE,
      pageSize: 20,
      search: '',
      ordering: DEFAULT_ORDERING,
    },
  });

  // Selectors
  readonly items = computed(() => this.state().items);
  readonly total = computed(() => this.state().total);
  readonly loading = computed(() => this.state().loading);
  readonly query = computed(() => this.state().query);

  loadItems(query?: Partial<ServerListQuery>) {
    const currentQuery = { ...this.state().query, ...query };
    
    // Si cambia búsqueda, volvemos a página 1
    if (query?.search !== undefined && query.search !== this.state().query.search) {
      currentQuery.page = 1;
    }

    this.state.update((s) => ({ ...s, loading: true, query: currentQuery }));

    this.apiService.listParams(currentQuery).subscribe({
      next: (response) => {
        this.state.update((s) => ({
          ...s,
          items: response.results,
          total: response.count,
          loading: false,
        }));
      },
      error: () => {
        this.state.update((s) => ({ ...s, loading: false }));
      },
    });
  }

  updateItem(id: number, data: Partial<CementoParametroDto>) {
    return this.apiService.updateParam(id, data).pipe(
      tap(() => {
        // Recargar para ver cambios
        this.loadItems();
      })
    );
  }

  setOrdering(value: string): void {
    const normalized = value.trim() || DEFAULT_ORDERING;
    if (this.state().query.ordering === normalized) {
      return;
    }
    this.loadItems({ ordering: normalized, page: DEFAULT_PAGE });
  }

  setPage(value: number): void {
    const page = Number.isFinite(value) ? Math.max(DEFAULT_PAGE, Math.trunc(value)) : DEFAULT_PAGE;
    if (this.state().query.page === page) {
      return;
    }
    this.loadItems({ page });
  }

  setPageSize(value: number): void {
    const pageSize = Number.isFinite(value) ? Math.max(1, Math.trunc(value)) : 20;
    if (this.state().query.pageSize === pageSize) {
      return;
    }
    this.loadItems({ pageSize, page: DEFAULT_PAGE });
  }

  setSearchTerm(value: string): void {
    const normalized = String(value ?? '');
    if (this.state().query.search === normalized) {
      return;
    }
    this.loadItems({ search: normalized, page: DEFAULT_PAGE });
  }
}
